/**
 * Private-mode key handling shared by the web app and the MCP server: PIN key
 * derivation, DEK wrap/unwrap, AES-GCM field encryption, the v1 verifier and
 * the recovery phrase. One copy, so both sides always derive and decrypt alike.
 * Uses globalThis.crypto (Web Crypto) — browsers and Node 20+.
 */

import { generateMnemonic, mnemonicToSeed } from "@scure/bip39"
import { wordlist } from "@scure/bip39/wordlists/english.js"
import { argon2id } from "hash-wasm"

// ─── v1 key derivation (LEGACY — kept only for v1→v2 migration) ──────────────
// DO NOT use for new-user setup or normal unlock.
export async function deriveKey(userId: string, pin: string, salt: string): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey("raw", enc.encode(userId + pin), "PBKDF2", false, [
    "deriveKey",
  ])
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode(salt), iterations: 100000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true, // must be extractable=true so we can export/wrap it during migration
    ["encrypt", "decrypt"],
  )
}

// ─── v2 KDF: Argon2id KEK derivation ─────────────────────────────────────────
// Returns a non-extractable AES-GCM CryptoKey for wrapping/unwrapping the DEK.
// Params: 64MB memory, t=3 iterations, p=1 parallelism, 32-byte output.
export async function deriveKEK(pin: string, kdfSaltB64: string): Promise<CryptoKey> {
  const saltBytes = Uint8Array.from(atob(kdfSaltB64), (c) => c.charCodeAt(0))
  const hash = await argon2id({
    password: pin,
    salt: saltBytes,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536, // 64 MB in KiB
    hashLength: 32,
    outputType: "binary",
  })
  // Copy into a fresh ArrayBuffer-backed view for importKey's BufferSource type.
  const keyBytes = new Uint8Array(hash)
  return crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM", length: 256 },
    false, // extractable: false — closes #15, KEK never leaves WebCrypto
    ["wrapKey", "unwrapKey"],
  )
}

// ─── Generate a random 16-byte KDF salt ──────────────────────────────────────
export function generateKdfSalt(): string {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  return btoa(String.fromCharCode(...salt))
}

// ─── Generate a random DEK (data-encryption key) ─────────────────────────────
// extractable: true so it can be wrapped via wrapKey for recovery storage.
export async function generateDEK(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"])
}

// ─── Wrap / unwrap DEK under KEK ─────────────────────────────────────────────
// Envelope: base64(12-byte iv || AES-GCM ciphertext of raw DEK)
export async function wrapDEK(dek: CryptoKey, kek: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const wrapped = await crypto.subtle.wrapKey("raw", dek, kek, { name: "AES-GCM", iv })
  const combined = new Uint8Array(12 + wrapped.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(wrapped), 12)
  return btoa(String.fromCharCode(...combined))
}

// Build the v2 PIN envelope. With `existingDek` (recovery flow) the SAME DEK is
// re-wrapped under the new PIN so existing ciphertext keeps decrypting; without
// it (first-time setup) a fresh DEK is minted.
export async function wrapDekForNewPin(
  pin: string,
  existingDek?: CryptoKey,
): Promise<{ dek: CryptoKey; kdfSalt: string; wrappedDek: string }> {
  const dek = existingDek ?? (await generateDEK())
  const kdfSalt = generateKdfSalt()
  const kek = await deriveKEK(pin, kdfSalt)
  const wrappedDek = await wrapDEK(dek, kek)
  return { dek, kdfSalt, wrappedDek }
}

export async function unwrapDEK(wrappedDekB64: string, kek: CryptoKey): Promise<CryptoKey> {
  const combined = Uint8Array.from(atob(wrappedDekB64), (c) => c.charCodeAt(0))
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)
  // unwrapKey returns an extractable:true key (required for recovery wrapKey)
  return crypto.subtle.unwrapKey(
    "raw",
    ciphertext,
    kek,
    { name: "AES-GCM", iv },
    { name: "AES-GCM", length: 256 },
    true, // extractable so recovery wrapping works
    ["encrypt", "decrypt"],
  )
}

const CIPHERTEXT_CHARSET_RE = /^[A-Za-z0-9+/]+={0,2}$/
// Our AES-GCM field format is base64(12-byte IV + ciphertext + 16-byte GCM tag).
// The smallest possible value (an encrypted empty string) is 12+16=28 raw bytes,
// which base64-encodes to 40 chars. Anything shorter, or that isn't base64
// alphabet, cannot be our ciphertext — treat it as plaintext (e.g. a
// transaction description imported before private mode was ever enabled) and
// leave it untouched rather than attempting (and failing) to decrypt it.
const MIN_CIPHERTEXT_B64_LEN = 40

/** Heuristic: does this string look like our AES-GCM field ciphertext? */
export function looksLikeCiphertext(value: string): boolean {
  return value.length >= MIN_CIPHERTEXT_B64_LEN && CIPHERTEXT_CHARSET_RE.test(value)
}

// ─── Encrypt / decrypt a single string field ─────────────────────────────────
export async function encryptField(plaintext: string, key: CryptoKey): Promise<string> {
  const enc = new TextEncoder()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(plaintext),
  )
  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.byteLength)
  return btoa(String.fromCharCode(...combined))
}

export async function decryptField(encrypted: string, key: CryptoKey): Promise<string> {
  const combined = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0))
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext)
  return new TextDecoder().decode(plaintext)
}

// ─── PIN verifier (v1 only — kept for v1 users not yet migrated) ──────────────
const VERIFIER_PLAINTEXT = "paisa-verify-v1"

export async function createVerifier(key: CryptoKey): Promise<string> {
  return encryptField(VERIFIER_PLAINTEXT, key)
}

export async function checkVerifier(key: CryptoKey, encryptedVerifier: string): Promise<boolean> {
  try {
    const decrypted = await decryptField(encryptedVerifier, key)
    return decrypted === VERIFIER_PLAINTEXT
  } catch {
    return false
  }
}

// ─── Key export/import (for recovery) ────────────────────────────────────────
export async function exportKey(key: CryptoKey): Promise<Uint8Array> {
  const raw = await crypto.subtle.exportKey("raw", key)
  return new Uint8Array(raw)
}

export async function importRawKey(raw: Uint8Array): Promise<CryptoKey> {
  const sliced = raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength)
  const buf = sliced instanceof ArrayBuffer ? sliced : new ArrayBuffer(0)
  return crypto.subtle.importKey("raw", buf, { name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ])
}

/**
 * The same AES-GCM key with extractable:false, so its bytes can't be exported or
 * wrapped. Used for the long-lived in-memory DEK: script running in the page can
 * still use it while unlocked, but can't copy it out.
 */
export async function toNonExtractable(key: CryptoKey): Promise<CryptoKey> {
  if (!key.extractable) return key
  const raw = await crypto.subtle.exportKey("raw", key)
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM", length: 256 }, false, [
    "encrypt",
    "decrypt",
  ])
}

/**
 * Non-extractable HMAC-SHA256 key over the same bytes as the household DEK.
 * Person search hashes use it, so the web (which only keeps a non-extractable
 * DEK) and MCP produce identical hashes. Needs the extractable key once, at unlock.
 */
export async function toSearchHmacKey(key: CryptoKey): Promise<CryptoKey> {
  const raw = await crypto.subtle.exportKey("raw", key)
  return crypto.subtle.importKey("raw", raw, { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
}

/** base64 HMAC-SHA256 of a value: the `searchHash` stored beside a private person. */
export async function searchHash(value: string, hmacKey: CryptoKey): Promise<string> {
  const sig = await crypto.subtle.sign("HMAC", hmacKey, new TextEncoder().encode(value))
  return btoa(String.fromCharCode(...new Uint8Array(sig)))
}

// ─── Recovery phrase ─────────────────────────────────────────────────────────
export function generateRecoveryMnemonic(): string {
  return generateMnemonic(wordlist, 128) // 12 words
}

export async function recoveryKeyFromMnemonic(mnemonic: string): Promise<CryptoKey> {
  const seed = await mnemonicToSeed(mnemonic)
  return crypto.subtle.importKey(
    "raw",
    seed.slice(0, 32),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  )
}

// Wrap user key with recovery key (stored on server)
export async function wrapKeyForRecovery(
  userKey: CryptoKey,
  recoveryKey: CryptoKey,
): Promise<string> {
  const rawUserKey = await exportKey(userKey)
  return encryptField(btoa(String.fromCharCode(...rawUserKey)), recoveryKey)
}

// Unwrap user key using recovery key
export async function unwrapKeyFromRecovery(
  wrappedKey: string,
  recoveryKey: CryptoKey,
): Promise<CryptoKey> {
  const rawBase64 = await decryptField(wrappedKey, recoveryKey)
  const raw = Uint8Array.from(atob(rawBase64), (c) => c.charCodeAt(0))
  return importRawKey(raw)
}
